import { toString } from '@pequity/shared/utils';
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, type MaybeRef, type Ref, toValue } from 'vue';
import { http } from '@/axios/instance/http';
import { createBulkUpdateFields } from '@/ccp-mfe/components/ccp-details/ccp-employees-table/ccp-action-bar/ccp-employees-table-bulk-update.config';
import {
  createAdminColsAndFields,
  createGroupColsAndFields,
} from '@/ccp-mfe/components/ccp-details/ccp-employees-table/ccp-employees-table.config';
import { createPermissionsInfoAdmin, createPermissionsInfoDefaults } from '@/ccp-mfe/pages/ccp-details.utils';
import { fetchCycleInfo } from '@/ccp-mfe/query/ccp-settings';
import {
  type AdminOrRoleId,
  CONFIG_API_LAYOUT_NAME,
  type ConfigApiUserRole,
  type CsvExportType,
  type CsvImportType,
  type DisplayLayout,
  type DisplayLayoutFlat,
  type Employee,
  type EmployeeFetchOptions,
  type EmployeeResponse,
  type FetchColdsAndFiendsParams,
  type FieldsConfigParams,
  type FilterOptionsResponse,
  type PermissionsInfo,
  type QueryObj,
  type SelectOptions,
  type SelectOptionsQueryKey,
  type SelectOptionsResponse,
} from '@/ccp-mfe/types';
import { setFilterStateInCols } from '@/ccp-mfe/utils/table';

// API helper - Saves a change to the employee model
export const updateEmployee = async (vars: {
  employeeId: number;
  employeeData: Partial<Employee>;
  planningRoleId: number | null;
}) => {
  const params = vars.planningRoleId ? { planning_role_id: vars.planningRoleId } : {};

  const res = await http.patch(`v2/ccp/employees/${vars.employeeId}`, vars.employeeData, { params });

  return res.data;
};

// API helper - Fields and columns fetcher
export const fetchFieldsConfig = async (cycle_id: number, params: FieldsConfigParams) => {
  if (!params.planning_role_id) {
    throw Error('You must define a role');
  }

  if (!CONFIG_API_LAYOUT_NAME.includes(params.layout_name)) {
    throw Error('You must define a valid layout name');
  }

  const { data } = await http.get<DisplayLayout[]>(`v3/ccp/layouts/cycle_layouts`, {
    params: {
      ...params,
      cycle_id,
      ...(params.planning_role_id === 'admin' && { planning_role_id: null }),
      ...(params.convert_to ? { convert_to: params.convert_to } : {}),
    },
  });

  return Array.isArray(data) ? data : [];
};

export const useFieldsConfigQuery = (
  vars: Ref<{ cycleId: number; planningRoleId: AdminOrRoleId; planningGroupId: number | null }>,
  options: { enabled: Ref<boolean> }
) => {
  return useQuery({
    queryKey: ['ccpFieldsConfig', vars],
    queryFn: () => {
      const { cycleId, planningRoleId, planningGroupId } = toValue(vars);

      return fetchFieldsConfig(cycleId, {
        planning_role_id: planningRoleId,
        planning_group_id: planningGroupId,
        layout_name: 'employees',
      });
    },
    enabled: options.enabled,
  });
};

// API helper - Fetches the ccp employees
const fetchEmployees = async (data: EmployeeFetchOptions & { cycleId: number }) => {
  const { convertTo, ...body } = data;
  const params = { role: data.role ?? 'planner', ...(convertTo ? { convert_to: convertTo } : {}) };

  const res = await http.post<EmployeeResponse>(`v2/ccp/cycles/${data.cycleId}/employees/filtered`, body, {
    params,
  });

  return res.data;
};

// API helper - Fetches the cpp employees table fields and columns
const fetchColsAndFields = async (data: FetchColdsAndFiendsParams) => {
  const cycleInfoRes = await fetchCycleInfo({
    cycleId: data.cycleId,
    role: data.roleId === 'admin' ? 'admin' : 'planner',
  });
  const isAvatarVisible = cycleInfoRes.data.is_avatar_visible;

  if (data.roleId === 'admin') {
    const { cols, fields } = await createAdminColsAndFields({
      cycleId: data.cycleId,
      isAvatarVisible,
      convertTo: data.convertTo,
    });

    return { cols: setFilterStateInCols(cols, data.filters, data.ordering), fields };
  }

  if (typeof data.roleId === 'number') {
    const { cols, fields } = await createGroupColsAndFields({
      cycleId: data.cycleId,
      roleId: data.roleId,
      planningGroupId: data.planningGroupId,
      isAvatarVisible,
      convertTo: data.convertTo,
    });

    return { cols: setFilterStateInCols(cols, data.filters, data.ordering), fields };
  }
};

// API helper - Fetches the cycle permissions info
export const fetchCyclePermissionsInfo = async (data: { roleId: AdminOrRoleId }) => {
  const { roleId } = data;

  if (roleId === 'admin') {
    return createPermissionsInfoAdmin();
  }

  const res = await http.get<PermissionsInfo>(`v2/ccp/roles/${roleId}/info`);

  return { ...createPermissionsInfoDefaults(), ...res.data };
};

// Fetches the cycle permissions info
export const useCyclePermissionsInfoQuery = (vars: MaybeRef<{ roleId: AdminOrRoleId }>) => {
  return useQuery({
    queryKey: ['cyclePermissionsInfo', vars],
    queryFn: () => fetchCyclePermissionsInfo(toValue(vars)),
    placeholderData: () => createPermissionsInfoDefaults(),
    staleTime: 1500,
  });
};

// Fetches the ccp employees
export const useEmployeesQuery = (vars: Ref<EmployeeFetchOptions & { cycleId: number }>) => {
  return useQuery({
    queryKey: ['ccpEmployees', vars],
    queryFn: () => fetchEmployees(toValue(vars)),
    placeholderData: (previousData) => {
      // Only keep previous data if it actually had results
      // Otherwise, show loading state instead of empty state during refetch
      return previousData?.results?.length ? previousData : undefined;
    },
  });
};

// Fetches the ccp employee page number
export const useEmployeePageNumberQuery = (
  vars: Ref<EmployeeFetchOptions & { cycleId: number; employeeId: number }>,
  options: { enabled: Ref<boolean> }
) => {
  return useQuery({
    queryKey: ['ccpEmployeePageNumber', vars],
    queryFn: async () => {
      const data = toValue(vars);

      const res = await http.post<{ page: number | null }>(
        `v2/ccp/cycles/${data.cycleId}/employees/${data.employeeId}/get_page`,
        data
      );

      return Number(res.data.page);
    },
    enabled: options.enabled,
  });
};

// Fetches the cpp employees table fields and columns
export const useColsAndFieldsQuery = (
  vars: Ref<{
    cycleId: number;
    roleId: AdminOrRoleId;
    filters: QueryObj['filters'];
    ordering: QueryObj['ordering'];
    planningGroupId: number | null;
    convertTo?: string;
  }>,
  options: { enabled: Ref<boolean> }
) => {
  return useQuery({
    queryKey: ['ccpColsAndFields', vars],
    queryFn: () => fetchColsAndFields(toValue(vars)),
    enabled: options.enabled,
    placeholderData: keepPreviousData,
  });
};

// Updates the ccp employee model
export const useUpdateEmployeeMutation = () => {
  return useMutation({
    mutationFn: updateEmployee,
  });
};

// Updates the field config (used for column resizing)
export const useUpdateFieldConfigMutation = () => {
  return useMutation({
    mutationFn: async (vars: { columnId: number; displayLayout: Partial<DisplayLayout> }) => {
      return http.patch(`v2/ccp/layouts/${vars.columnId}/update_style`, {
        ...vars.displayLayout,
        width: vars.displayLayout?.style?.['max-width'] || vars.displayLayout?.content_style?.['max-width'],
      });
    },
  });
};

// Approves the comp cycle
export const useApproveCompCycleMutation = () => {
  return useMutation({
    mutationFn: async (groupId: number | string) => {
      return http.post<void>(`v2/ccp/groups/${groupId}/approve`);
    },
  });
};

// Downloads a CSV template
export const useDownloadCsvTemplateMutation = () => {
  return useMutation({
    mutationFn: (vars: { cycleId: number; csvImportType: CsvImportType }) => {
      const params = vars.csvImportType ? { template_type: vars.csvImportType } : {};

      return http.get<Blob>(`v2/ccp/cycles/${vars.cycleId}/templates`, {
        params,
        responseType: 'blob',
      });
    },
  });
};

// Imports a CSV template file
export const useImportCsvFileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number; csvImportType: CsvImportType; csvFile: File }) => {
      const formData = new FormData();

      formData.append('cycle_id', toString(vars.cycleId));
      formData.append('import_type', vars.csvImportType);
      if (vars.csvFile instanceof File) {
        formData.append('csv_file', vars.csvFile);
      }

      return http.post<{ id?: number; error?: string }>(`ccp/imports/import_csv`, formData);
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
    },
  });
};

// Mirrors the backend CsvImportStatus IntegerChoices
export const CSV_IMPORT_STATUS = {
  PENDING: 0,
  IN_PROGRESS: 1,
  SUCCESS: 2,
  ERROR: 3,
} as const;

// Polls a CSV import's status so the import modal can wait for the async task to finish
export const useCsvImportStatusQuery = (
  vars: Ref<{ importId: number | null }>,
  options: { enabled: MaybeRef<boolean> } = { enabled: true }
) => {
  return useQuery({
    queryKey: ['csvImportStatus', vars],
    queryFn: async () => {
      const { importId } = toValue(vars);
      const res = await http.get<{ id: number; status: number }>(`ccp/imports/${importId}`);

      return res.data;
    },
    enabled: options.enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === CSV_IMPORT_STATUS.SUCCESS || status === CSV_IMPORT_STATUS.ERROR) {
        return false;
      }
      return 2000;
    },
  });
};

// Downloads a CSV template
export const useExportCsvMutation = () => {
  return useMutation({
    mutationFn: (vars: {
      cycleId: number;
      exportType: CsvExportType;
      planning_role_id: number;
      columns?: string[];
      convertTo?: string;
    }) => {
      return http.post<void>(`ccp/exports/generate`, {
        cycle_id: vars.cycleId,
        export_type: vars.exportType,
        planning_role_id: vars.planning_role_id,
        columns: vars.columns ?? [],
        ...(vars.convertTo ? { convert_to: vars.convertTo } : {}),
      });
    },
  });
};

// Fetches the select options for the given fields
export const useFieldValuesQueries = (vars: Ref<SelectOptionsQueryKey>) => {
  const queries = computed(() => {
    return vars.value.map((opts) => ({
      queryKey: ['fieldValues', opts],
      queryFn: async () => {
        const params = {
          role: opts.role,
          ...(opts.planningGroupId && { planning_group_id: opts.planningGroupId }),
          ...(opts.planningRoleId && { planning_role_id: opts.planningRoleId }),
        };

        const res = await http.get<SelectOptionsResponse>(`v2/ccp/layouts/${opts.id}/field_values`, { params });

        return res.data;
      },
    }));
  });

  return useQueries({
    queries,
    combine: (results) => {
      const data = results.reduce<SelectOptions>((acc, curr, i) => {
        return { ...acc, [vars.value[i].name]: curr.data?.select_options ?? [] };
      }, {});

      return {
        data,
        isPending: results.some((result) => result.isPending),
        isFetching: results.some((result) => result.isFetching),
        isError: results.some((result) => result.isError),
      };
    },
  });
};

// Fetches the filters options for the given column
export const useFilterOptionsQuery = (
  vars: Ref<EmployeeFetchOptions & { cycleId: number; colId: number }>,
  options: { enabled: Ref<boolean> }
) => {
  return useQuery({
    queryKey: ['filterOptions', vars],
    queryFn: async () => {
      const data = toValue(vars);
      const params = { role: data.role, column_id: data.colId, planning_group_id: data.planning_group_id };

      const res = await http.post<FilterOptionsResponse>(
        `v2/ccp/cycles/${data.cycleId}/employees/filter_options`,
        data,
        { params }
      );

      return res.data.filter_options;
    },
    enabled: options.enabled,
  });
};

// Query keys for prefetching the ccp details page for admins
const colsAndFieldsQueryKey = (cycleId: number): [string, FetchColdsAndFiendsParams] => {
  const vars = {
    cycleId,
    roleId: 'admin',
    filters: {},
    ordering: [],
    planningGroupId: null,
  } satisfies FetchColdsAndFiendsParams;

  return ['ccpColsAndFields', vars];
};

const createEmployeesQueryKey = (cycleId: number): [string, EmployeeFetchOptions & { cycleId: number }] => {
  const vars = {
    cycleId,
    filters: { order: [] },
    only_with_comments: false,
    has_not_been_sent_reward_letter: false,
    has_formula_errors: false,
    ordering: [],
    page: '1',
    page_size: 100,
    planning_group_id: null,
    planning_role_id: null,
    role: 'admin',
    search: '',
  } satisfies EmployeeFetchOptions & { cycleId: number };

  return ['ccpEmployees', vars];
};

// Prefetches the ccp employees table for admins
export const usePrefetchCcpDetailsAdmin = () => {
  const queryClient = useQueryClient();

  return {
    prefetchCcpDetailsAdmin(cycleId: number) {
      const colsAndFieldsKey = colsAndFieldsQueryKey(cycleId);
      const employeesKey = createEmployeesQueryKey(cycleId);

      queryClient.prefetchQuery({
        queryKey: ['cycleInfo', { cycleId, role: 'admin' }],
        queryFn: async () => {
          const res = await fetchCycleInfo({ cycleId, role: 'admin' });

          return res.data;
        },
      });
      queryClient.prefetchQuery({ queryKey: colsAndFieldsKey, queryFn: () => fetchColsAndFields(colsAndFieldsKey[1]) });
      queryClient.prefetchQuery({ queryKey: employeesKey, queryFn: () => fetchEmployees(employeesKey[1]) });
    },
  };
};

// Uses the display layouts flat endpoint to return a list of fields for the bulk update modal
export const useBulkUpdateFieldsQuery = (
  vars: Ref<{ cycleId: number; groupId: number | null; roleId: number | null; role: ConfigApiUserRole | null }>,
  options: { enabled: Ref<boolean> }
) => {
  return useQuery({
    queryKey: ['ccpBulkUpdateFields', vars],
    queryFn: async () => {
      const { cycleId, groupId, roleId, role } = toValue(vars);

      const params = {
        cycle_id: cycleId,
        ...(role ? { role } : {}),
        ...(roleId ? { planning_role_id: roleId } : {}),
        ...(groupId ? { planning_group_id: groupId } : {}),
      };

      const res = await http.get<DisplayLayoutFlat[]>(`v2/ccp/layouts/flat`, { params });

      return createBulkUpdateFields(res.data);
    },
    enabled: options.enabled,
  });
};

// Updates one field for multiple employees
export const useBulkUpdateFieldsMutation = () => {
  return useMutation({
    mutationFn: async (vars: {
      employeeIds: number[];
      employeeEids: string[];
      displayLayoutId: number;
      newValue: unknown;
      groupId: number | null;
      role: ConfigApiUserRole;
      roleId: number | null;
    }) => {
      const data = {
        employee_ids: vars.employeeIds,
        employee_eids: vars.employeeEids,
        column_id: vars.displayLayoutId,
        new_value: vars.newValue,
        ...(vars.groupId && { planning_group_id: vars.groupId }),
        ...(vars.roleId && { planning_role_id: vars.roleId }),
        ...(vars.role && { role: vars.role }),
      };

      return http.patch(`v2/ccp/employees/bulk_update`, data);
    },
  });
};

// Deletes multiple employees
export const useBulkDeleteEmployeesMutation = () => {
  return useMutation({
    mutationFn: async (vars: { employeeIds: number[] }) => {
      return http.delete<void>(`v2/ccp/employees/bulk_delete`, { data: { employee_ids: vars.employeeIds } });
    },
  });
};

// Syncs employees from employee v2
export const useSyncEmployeesMutation = () => {
  return useMutation({
    mutationFn: async (vars: { cycleId: number; ccpEmployeeIds: number[] }) => {
      return http.post<void>(`v2/ccp/employees/sync_all_layouts_from_employee_v2`, {
        cycle_id: vars.cycleId,
        ccp_employee_ids: vars.ccpEmployeeIds,
      });
    },
  });
};

// Re-executes all RANGELOOKUP formulas and their dependents
export const useRerunRangeLookupFormulasMutation = () => {
  return useMutation({
    mutationFn: async (vars: { cycleId: number }) => {
      return http.post<void>('v2/ccp/excel_formulas/rerun_range_lookups/', {
        comp_cycle_id: vars.cycleId,
      });
    },
  });
};

// Removes terminated employees from compensation cycle
export const useRemoveTerminatedEmployeesMutation = () => {
  return useMutation({
    mutationFn: async (vars: { cycleId: number }) => {
      const response = await http.post<{ detail?: string }>(
        `v2/ccp/cycles/${vars.cycleId}/remove-terminated-employees/`
      );
      return response.data;
    },
  });
};
